import { handbookHref } from "@/lib/help-center-seo";
import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { Logo } from "../hero-primitives";
import { H } from "../hero-tokens";
import { OPEN_POSITIONS_COUNT } from "../open-positions-count";

/**
 * SECTION 1 — Hero top nav bar (1:1 with the prototype, lines 29–45).
 *
 * Container max-width 1240, height 64. Three flex groups:
 *  - left: Logo lockup (30px square + triangle + "hausverwalter" wordmark)
 *  - center: Preise (/preise) + Spar-Rechner (/preise#spar-rechner) + Handbuch (docs host) + Karriere
 *  - right: Anmelden (/login) + green "Demo buchen" button (/demo)
 */
export function HeroNav(): ReactNode {
  const t = useTranslations("marketing.heroNav");
  return (
    <nav className="mkhero-nav">
      {/* Left — logo lockup */}
      <Link
        href="/"
        className="mkhero-nav-logo"
        aria-label={BRAND_HOME_ARIA_LABEL}
        title={BRAND_LOGO_TITLE}
        style={{ display: "inline-flex", textDecoration: "none" }}
      >
        <Logo size={30} />
      </Link>

      {/* Center — marketing links (hidden on narrow tablet via CSS) */}
      <div className="mkhero-nav-center">
        <Link
          href="/preise"
          className="mkhero-nav-link"
          style={{
            fontSize: 14.5,
            color: "rgba(11,15,25,0.7)",
            textDecoration: "none",
          }}
        >
          {t("preise")}
        </Link>
        <Link
          href={"/preise#spar-rechner"}
          className="mkhero-nav-link"
          style={{
            fontSize: 14.5,
            color: "rgba(11,15,25,0.7)",
            textDecoration: "none",
          }}
        >
          {t("sparRechner")}
        </Link>
        <a
          href={handbookHref()}
          className="mkhero-nav-link"
          style={{
            fontSize: 14.5,
            color: "rgba(11,15,25,0.7)",
            textDecoration: "none",
          }}
        >
          {t("handbuch")}
        </a>
        <Link
          href="/careers"
          className="mkhero-nav-link"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14.5,
            color: "rgba(11,15,25,0.7)",
            textDecoration: "none",
          }}
        >
          {t("karriere")}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 17,
              height: 17,
              padding: "0 5px",
              borderRadius: 999,
              background: H.accent,
              color: "#fff",
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {OPEN_POSITIONS_COUNT}
          </span>
        </Link>
      </div>

      {/* Right — auth + primary CTA (never shrinks / clips) */}
      <div className="mkhero-nav-actions">
        <Link
          href="/login"
          className="mkhero-nav-login"
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            color: H.ink,
            textDecoration: "none",
          }}
        >
          {t("anmelden")}
        </Link>
        <Link
          href="/demo"
          className="mkhero-darkbtn mkhero-nav-demo"
          style={{
            background: H.accent,
            color: "#fff",
            fontSize: 14.5,
            fontWeight: 600,
            padding: "10px 18px",
            borderRadius: 10,
            textDecoration: "none",
          }}
        >
          {t("demoBuchen")}
        </Link>
      </div>
    </nav>
  );
}
